import { type ConsentInfoResponse, Theme } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import BrandingHeader from '@/components/BrandingHeader';
import { useIsAimsLayout } from '@/hooks/use-layout-mode';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import FirstScreenLayout from '../FirstScreenLayout';

import styles from './index.module.scss';

type ThirdPartyBranding = ConsentInfoResponse['application']['branding'];

type Props = {
  readonly children: ReactNode;
  readonly title: TFuncKey;
  readonly titleInterpolation?: Record<string, unknown>;
  readonly thirdPartyBranding?: ThirdPartyBranding;
};

/** Applicants read a plain line under the title: what signing in is for, or how sign-up works. */
type ApplicantLine =
  | 'description.aims_layout.applicant_sign_in'
  | 'description.aims_layout.applicant_register';

const applicantSubtitle = (title: TFuncKey): ApplicantLine | undefined =>
  title === 'description.sign_in_to_your_account'
    ? 'description.aims_layout.applicant_sign_in'
    : title === 'description.create_your_account'
      ? 'description.aims_layout.applicant_register'
      : undefined;

const LandingPageLayout = ({ children, title, titleInterpolation, thirdPartyBranding }: Props) => {
  const { experienceSettings, theme: pageTheme } = useContext(PageContext);
  const { t } = useTranslation();
  const isAimsLayout = useIsAimsLayout();

  if (!experienceSettings) {
    return null;
  }

  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;
  // The AIMS layout is light-only, so its crest is always the light logo.
  const theme = isAimsLayout ? Theme.Light : pageTheme;
  // On the AIMS layout the sign-in page reads "Sign in" over "<organisation> staff".
  const isAimsSignIn = isAimsLayout && title === 'description.sign_in_to_your_account';
  const headline = isAimsSignIn ? 'description.sign_in' : title;
  const isApplicant = isAimsLayout && branding.aimsAudience === 'applicant';
  const applicantLine = isApplicant ? applicantSubtitle(title) : undefined;
  const subtitle = isApplicant
    ? applicantLine && t(applicantLine)
    : isAimsSignIn && branding.splitHeadline
      ? t('description.aims_layout.staff', { name: branding.splitHeadline })
      : undefined;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title, titleKeyInterpolation: titleInterpolation }}>
      <BrandingHeader
        className={classNames(styles.header, layoutClassNames.brandingHeader)}
        headline={headline}
        headlineInterpolation={titleInterpolation}
        welcomeText={branding.welcomeHeadline}
        subtitle={subtitle}
        // Applicants see the crest on the panel (signed-off mockup), not over the form.
        logo={isApplicant ? undefined : getBrandingLogoUrl({ theme, branding, isDarkModeEnabled })}
        thirdPartyLogo={
          thirdPartyBranding &&
          getBrandingLogoUrl({ theme, branding: thirdPartyBranding, isDarkModeEnabled })
        }
      />
      {children}
    </FirstScreenLayout>
  );
};

export default LandingPageLayout;
